import React from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Text } from 'shared/components'
import { InWinImage } from 'routes/shared/components'

import * as styles from './styles'

const MobileBeneficiaryCard = ({ beneficiary, isFollowed = false, activeFilter, onFavoriteToggle }) => {
	// Handle both regular beneficiaries and favorites (which have beneficiaryDTO structure)
	const actualBeneficiary = beneficiary.beneficiaryDTO || beneficiary

	const {
		logo,
		beneficiaryName,
		category = [],
		subCategory = [],
	} = actualBeneficiary

	const handleFavoriteClick = () => {
		if (onFavoriteToggle) {
			onFavoriteToggle(beneficiary, isFollowed)
		}
	}

	const renderCategoryTag = () => {
		if (!category || category.length === 0) return null

		// If we're on "All" filter, show all categories comma-separated
		if (activeFilter === 'all') {
			const allCategoryTexts = category.map(cat => {
				return typeof cat === 'string' ? cat : (cat?.description || 'Unknown')
			}).join(', ')

			return (
				<span css={styles.categoryTag}>
					{allCategoryTexts}
				</span>
			)
		}

		// If we're filtering by a specific category, show only that category
		if (activeFilter && activeFilter !== 'favorite') {
			// Find the filtered category in the beneficiary's categories
			const displayCategory = category.find(cat => {
				const catText = typeof cat === 'string' ? cat : cat?.description
				return catText && catText.toLowerCase() === activeFilter.toLowerCase()
			})

			if (displayCategory) {
				const categoryText = typeof displayCategory === 'string'
					? displayCategory
					: (displayCategory?.description || 'Unknown Category')

				return (
					<span css={styles.categoryTag}>
						{categoryText}
					</span>
				)
			}
		}

		// Fallback to first category (for favorite tab or if filtered category not found)
		const fallbackCategory = category[0]
		const categoryText = typeof fallbackCategory === 'string'
			? fallbackCategory
			: (fallbackCategory?.description || 'Unknown Category')

		return (
			<span css={styles.categoryTag}>
				{categoryText}
			</span>
		)
	}

	const renderCategoryLines = () => {
		if (!subCategory || subCategory.length === 0) return null

		return subCategory.slice(0, 4).map((subCat) => {
			const subCategoryText = typeof subCat === 'string'
				? subCat
				: (subCat?.description || 'Unknown Subcategory')
			const subCategoryKey = typeof subCat === 'string'
				? subCat
				: (subCat?.id || subCategoryText)

			return (
				<div key={subCategoryKey} css={styles.categoryLine}>
					<Text size={8} gray>
						{subCategoryText}
					</Text>
				</div>
			)
		})
	}

	return (
		<div css={styles.cardContainer}>
			<div css={styles.cardContent}>
				<div css={styles.leftSection}>
					<div css={styles.logoContainer}>
						{logo ? (
							<InWinImage
								source={logo}
								alt={beneficiaryName}
								css={styles.logo}
							/>
						) : (
							<div css={styles.logoPlaceholder}>
								<FontAwesomeIcon icon="building" size="2x" color="#ccc" />
							</div>
						)}
					</div>
				</div>

				<div css={styles.contentSection}>
					<div css={styles.categoryTagContainer}>
						{renderCategoryTag()}
					</div>

					<div css={styles.categoryLinesContainer}>
						{renderCategoryLines()}
					</div>

					<div css={styles.nameContainer}>
						<Text size={16} bold white css={styles.beneficiaryName}>
							{beneficiaryName}
						</Text>
					</div>
				</div>

				<div css={styles.actionSection}>
					<button
						css={styles.favoriteButton}
						onClick={handleFavoriteClick}
						aria-label={isFollowed ? 'Remove from favorites' : 'Add to favorites'}
					>
						<FontAwesomeIcon
							icon={isFollowed ? 'bookmark' : ['far', 'bookmark']}
							color={isFollowed ? '#00d4aa' : '#ffffff'}
							size="lg"
						/>
					</button>
				</div>
			</div>
		</div>
	)
}

MobileBeneficiaryCard.propTypes = {
	beneficiary: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		logo: PropTypes.string,
		beneficiaryName: PropTypes.string.isRequired,
		followed: PropTypes.bool,
		beneficiaryDTO: PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			logo: PropTypes.string,
			beneficiaryName: PropTypes.string.isRequired,
			followed: PropTypes.bool,
			category: PropTypes.arrayOf(
				PropTypes.oneOfType([
					PropTypes.string,
					PropTypes.shape({
						description: PropTypes.string,
					}),
				])
			),
			subCategory: PropTypes.arrayOf(
				PropTypes.oneOfType([
					PropTypes.string,
					PropTypes.shape({
						description: PropTypes.string,
					}),
				])
			),
		}),
		category: PropTypes.arrayOf(
			PropTypes.oneOfType([
				PropTypes.string,
				PropTypes.shape({
					id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
					description: PropTypes.string,
				}),
			])
		),
		subCategory: PropTypes.arrayOf(
			PropTypes.oneOfType([
				PropTypes.string,
				PropTypes.shape({
					id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
					description: PropTypes.string,
				}),
			])
		),
	}).isRequired,
	isFollowed: PropTypes.bool,
	activeFilter: PropTypes.string,
	onFavoriteToggle: PropTypes.func,
}

MobileBeneficiaryCard.defaultProps = {
	isFollowed: false,
	activeFilter: 'all',
}

export default MobileBeneficiaryCard 
